import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import Preloader from '../components/Preloader'
import Header from '../components/Header'
import ScrollReveal from '../components/ScrollReveal'
import Footer from '../components/Footer'
import { MdOutlineDesignServices } from 'react-icons/md'
import { FaRocket, FaChartLine, FaUsers } from 'react-icons/fa'

const highlights = [
    {
        title: 'Our story',
        description: 'At VRIFY TECHNOLOGIES, we create immersive reality experiences that help businesses and audiences connect in ways that feel instantly memorable.'
    },
    {
        title: 'What we do',
        description: 'From virtual tours and AR campaigns to tailored digital experiences, we combine strategy, design, and technology to deliver standout solutions.'
    },
    {
        title: 'Why clients choose us',
        description: 'Our team blends creativity, logistics, and technical excellence so every project is guided by professionalism, accessibility, and measurable results.'
    }
]

const values = [
    { title: 'Innovation', description: 'We stay ahead of emerging media, immersive design, and digital engagement trends.' },
    { title: 'Accessibility', description: 'Every experience is designed to be inclusive, responsive, and easy to navigate.' },
    { title: 'Collaboration', description: 'We work closely with clients to align brand goals, user needs, and business outcomes.' },
    { title: 'Professionalism', description: 'Our process is clear, responsive, and shaped around impact, quality, and trust.' },
    { title: 'Integrity', description: 'We communicate honestly, deliver on promises, and keep the customer journey at the center.' }
]

const AboutUs = () => {
    const sliderRef = useRef(null)
    const [isDragging, setIsDragging] = useState(false)
    const [isPaused, setIsPaused] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)
    const [currentIndex, setCurrentIndex] = useState(0)

    const slides = [
        { src: '../assets/pendulummedia.jpg', alt: 'partner logo' },
        { src: '../assets/89devsblacklogo.jpg', alt: 'partner logo' },
        { src: '../assets/Syteca-logo-color_small.png', alt: 'partner logo' },
        { src: '../assets/VRIFY_Africa_full_logo[1].png', alt: 'partner logo' },
        { src: '../assets/pendulummedia.jpg', alt: 'partner logo' },
        { src: '../assets/89devsblacklogo.jpg', alt: 'partner logo' },
        { src: '../assets/Syteca-logo-color_small.png', alt: 'partner logo' },
        { src: '../assets/VRIFY_Africa_full_logo[1].png', alt: 'partner logo' }
    ]

    const loopSlides = [...slides, ...slides, ...slides, ...slides]

    const handleMouseDown = (e) => {
        setIsDragging(true)
        setStartX(e.pageX - sliderRef.current.offsetLeft)
        setScrollLeft(sliderRef.current.scrollLeft)
    }

    const handleMouseLeave = () => {
        setIsDragging(false)
        setIsPaused(false)
    }

    const handleMouseUp = () => setIsDragging(false)

    const handleMouseMove = (e) => {
        if (!isDragging) return
        e.preventDefault()
        const x = e.pageX - sliderRef.current.offsetLeft
        const walk = (x - startX) * 1.5
        sliderRef.current.scrollLeft = scrollLeft - walk
    }

    const handleTouchStart = (e) => {
        setIsDragging(true)
        setStartX(e.touches[0].pageX - sliderRef.current.offsetLeft)
        setScrollLeft(sliderRef.current.scrollLeft)
    }

    const handleTouchEnd = () => setIsDragging(false)

    const handleTouchMove = (e) => {
        if (!isDragging) return
        const x = e.touches[0].pageX - sliderRef.current.offsetLeft
        const walk = (x - startX) * 1.5
        sliderRef.current.scrollLeft = scrollLeft - walk
    }

    useEffect(() => {
        if (sliderRef.current) {
            const slideWidth = 250 + 30
            sliderRef.current.scrollLeft = slides.length * slideWidth
        }
    }, [slides.length])

    useEffect(() => {
        const slider = sliderRef.current
        if (!slider) return

        const slideWidth = 250 + 30
        const totalSlides = slides.length
        const totalLoopSlides = loopSlides.length

        const handleScroll = () => {
            const maxScroll = slideWidth * (totalLoopSlides - totalSlides)
            if (slider.scrollLeft <= 0) {
                slider.scrollLeft = slideWidth * totalSlides
            } else if (slider.scrollLeft >= maxScroll) {
                slider.scrollLeft = slideWidth * totalSlides
            }

            const idx = Math.round(slider.scrollLeft / slideWidth) % totalSlides
            setCurrentIndex(idx)
        }

        slider.addEventListener('scroll', handleScroll)
        return () => slider.removeEventListener('scroll', handleScroll)
    }, [slides.length, loopSlides.length])

    const scrollToSlide = useCallback((idx) => {
        if (!sliderRef.current) return
        const slideWidth = 250 + 30
        sliderRef.current.scrollTo({
            left: (slides.length + idx) * slideWidth,
            behavior: 'smooth'
        })
    }, [slides.length])

    const goToPrev = useCallback(() => {
        scrollToSlide((currentIndex - 1 + slides.length) % slides.length)
    }, [currentIndex, scrollToSlide, slides.length])

    const goToNext = useCallback(() => {
        scrollToSlide((currentIndex + 1) % slides.length)
    }, [currentIndex, scrollToSlide, slides.length])

    useEffect(() => {
        if (isPaused || isDragging) return

        const timer = window.setInterval(() => {
            goToNext()
        }, 3000)

        return () => window.clearInterval(timer)
    }, [isPaused, isDragging, goToNext])

    return (
        <div>
            <Helmet>
                <title>About Us | VRIFY Technologies</title>
                <meta
                    name="description"
                    content="Learn about VRIFY Technologies - our vision, mission, values, and commitment to delivering immersive VR and AR solutions across Africa."
                />
                <meta
                    name="keywords"
                    content="about VRIFY, virtual reality company, AR company, immersive technology, vision, mission, virtual tours in ghana, tourism in ghana, tourism in africa, tourism, tech"
                />
                <meta property="og:title" content="About VRIFY Technologies" />
                <meta property="og:description" content="Discover VRIFY Technologies - leading provider of immersive VR and AR experiences." />
            </Helmet>

            <div className="font-nanum-myeongjo min-h-screen bg-slate-950 text-slate-100 pt-16 md:pt-20 lg:pt-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.24),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.16),_transparent_20%)]" />

                <div className="relative z-10">
                    <Header />
                    <Preloader />

                    <section className="mx-auto max-w-7xl px-4 pb-8 pt-8 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
                                <div>
                                    <p className="inline-flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">
                                        <MdOutlineDesignServices className="text-sm" />
                                        About us
                                    </p>
                                    <h1 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                        Immersive technology with a human-centered approach.
                                    </h1>
                                    <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
                                        VRIFY Technologies helps brands, institutions, and tourism leaders create memorable digital experiences that are engaging, accessible, and built to perform.
                                    </p>

                                    <div className="mt-8 flex flex-wrap gap-3">
                                        <a href="/contactus" className="inline-flex items-center justify-center rounded-full bg-yellow-400 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-yellow-300">
                                            Book a consultation
                                        </a>
                                        <a href="#partners" className="inline-flex items-center justify-center rounded-full border border-slate-700 px-5 py-3 text-sm font-semibold text-white transition hover:border-yellow-300 hover:text-yellow-200">
                                            See our partners
                                        </a>
                                    </div>
                                </div>

                                <div className="rounded-[28px] border border-white/10 bg-slate-900/85 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.45)] backdrop-blur sm:p-6">
                                    <div className="rounded-[24px] bg-gradient-to-br from-yellow-400 via-amber-300 to-slate-100 p-[1px]">
                                        <div className="rounded-[23px] bg-slate-950/95 p-5 sm:p-6">
                                            <p className="text-xs uppercase tracking-[0.3em] text-yellow-300">What guides us</p>
                                            <div className="mt-4 space-y-4">
                                                {highlights.map((item) => (
                                                    <div key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                                        <p className="text-sm font-semibold text-white">{item.title}</p>
                                                        <p className="mt-2 text-sm leading-7 text-slate-300">{item.description}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>

                    <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="grid gap-4 md:grid-cols-3">
                                <div className="rounded-[24px] border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                                    <FaRocket className="text-2xl text-yellow-300" />
                                    <p className="mt-3 text-lg font-semibold text-white">Vision</p>
                                    <p className="mt-2 text-sm leading-7 text-slate-300">To revolutionize how people interact, learn, and experience the world through immersive technology.</p>
                                </div>
                                <div className="rounded-[24px] border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                                    <FaChartLine className="text-2xl text-yellow-300" />
                                    <p className="mt-3 text-lg font-semibold text-white">Mission</p>
                                    <p className="mt-2 text-sm leading-7 text-slate-300">To empower businesses and individuals to harness the transformative power of VR and AR with confidence and clarity.</p>
                                </div>
                                <div className="rounded-[24px] border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                                    <FaUsers className="text-2xl text-yellow-300" />
                                    <p className="mt-3 text-lg font-semibold text-white">Core values</p>
                                    <p className="mt-2 text-sm leading-7 text-slate-300">Innovation, accessibility, collaboration, professionalism, and integrity guide every engagement we deliver.</p>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>

                    <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="flex flex-col gap-3 text-center sm:text-left">
                                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">Values</p>
                                <h2 className="text-2xl font-bold text-white sm:text-3xl">The principles that shape how we work.</h2>
                                <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                                    Every project is guided by a practical set of values designed to keep delivery clear, human-centered, and dependable.
                                </p>
                            </div>
                        </ScrollReveal>

                        <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {values.map((value) => (
                                <ScrollReveal key={value.title}>
                                    <div className="rounded-[24px] border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                                        <p className="text-sm font-semibold text-white">{value.title}</p>
                                        <p className="mt-2 text-sm leading-7 text-slate-300">{value.description}</p>
                                    </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </section>

                    <section id="partners" className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="flex flex-col gap-3 text-center sm:text-left">
                                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">Partners</p>
                                <h2 className="text-2xl font-bold text-white sm:text-3xl">Trusted by teams and brands building next-generation experiences.</h2>
                            </div>
                        </ScrollReveal>

                        <div className="mt-6 rounded-[28px] border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                            <div
                                ref={sliderRef}
                                onMouseEnter={() => setIsPaused(true)}
                                onMouseDown={handleMouseDown}
                                onMouseLeave={handleMouseLeave}
                                onMouseUp={handleMouseUp}
                                onMouseMove={handleMouseMove}
                                onTouchStart={handleTouchStart}
                                onTouchEnd={handleTouchEnd}
                                onTouchMove={handleTouchMove}
                                className="flex gap-4 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden"
                                style={{ scrollbarWidth: 'none' }}
                            >
                                {loopSlides.map((slide, index) => (
                                    <div key={`${slide.src}-${index}`} className="min-w-[250px] rounded-[20px] border border-white/10 bg-slate-950/80 p-4">
                                        <img src={slide.src} alt={slide.alt} className="h-24 w-full object-contain" loading="lazy" />
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 flex items-center justify-between gap-4">
                                <p className="text-sm text-slate-300">Partner spotlight {currentIndex + 1} of {slides.length}</p>
                                <div className="flex gap-2">
                                    <button onClick={goToPrev} className="rounded-full border border-white/10 px-4 py-2 text-sm text-white transition hover:border-yellow-300 hover:text-yellow-200">Prev</button>
                                    <button onClick={goToNext} className="rounded-full bg-yellow-400 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-yellow-300">Next</button>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="rounded-[30px] border border-yellow-300/30 bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-200 px-5 py-8 sm:px-8 lg:flex lg:items-center lg:justify-between lg:px-10">
                                <div className="max-w-2xl">
                                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-950">Ready to collaborate</p>
                                    <h2 className="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">Turn your next idea into a memorable experience.</h2>
                                    <p className="mt-3 text-sm leading-7 text-slate-900/80 sm:text-base">
                                        Whether you need a new interactive campaign, a virtual tour, or a full digital experience, we can help you launch with confidence.
                                    </p>
                                </div>
                                <div className="mt-6 flex flex-wrap gap-3 lg:mt-0">
                                    <a href="/contactus" className="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-yellow-300 transition hover:bg-slate-900">
                                        Contact us
                                    </a>
                                    <a href="/virtual-tours" className="inline-flex items-center justify-center rounded-full border border-slate-950 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-slate-950 hover:text-yellow-300">
                                        Explore services
                                    </a>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>
                </div>
            </div>

            <Footer />
        </div>
    )
}

export default AboutUs
